import type { ResponsiveStyleValue } from '@mui/system';
import { makeStyles } from 'tss-react/mui';
import { createResponsiveStyles } from '@/utils/styles';
import { DropdownSize } from './Dropdown';

const darkOrLightStyle =
  (isOnDark: boolean) => (lightValue: string, darkValue: string) =>
    isOnDark ? darkValue : lightValue;

const useStyles = makeStyles<{
  size: ResponsiveStyleValue<DropdownSize>;
  isOnDark: boolean;
}>({
  name: 'Dropdown',
})(({ brandColors, typography, type, breakpoints }, { size, isOnDark }) => {
  const backgroundColor = darkOrLightStyle(isOnDark)(
    brandColors.white,
    brandColors.tintsHeroBlue10
  );

  const labelColor = darkOrLightStyle(isOnDark)(
    brandColors.tintsNeutral20,
    brandColors.white
  );

  const helperColor = darkOrLightStyle(isOnDark)(
    brandColors.tintsNeutral20,
    brandColors.white
  );

  const defaultBorderColor = brandColors.tintsNeutral30;

  const hoverBorderColor = darkOrLightStyle(isOnDark)(
    brandColors.black,
    brandColors.white
  );

  const focusBorderColor = brandColors.chargeBlue;

  const errorBorderColor = darkOrLightStyle(isOnDark)(
    brandColors.semanticColorsError50,
    brandColors.semanticColorsError60
  );

  const iconColor = darkOrLightStyle(isOnDark)(
    brandColors.black,
    brandColors.white
  );

  const getSizeStyles = (size: DropdownSize) => {
    switch (size) {
      case DropdownSize.SM:
        return {
          '& .MuiSelect-select': {
            padding: `${typography.pxToRem(10)} ${typography.pxToRem(16)}`,
          },
        };
      case DropdownSize.LG:
        return {
          '& .MuiSelect-select': {
            padding: typography.pxToRem(16),
          },
        };

      default:
        return {};
    }
  };

  return {
    wrapper: {
      display: 'flex',
      gap: typography.pxToRem(9),
      '& .MuiFormHelperText-root': {
        marginLeft: 0,
      },
    },
    label: {
      ...type['14_regular'],
      display: 'flex',
      alignItems: 'center',
      gap: typography.pxToRem(8),
      color: labelColor,
      '&.Mui-disabled': {
        opacity: '0.4',
        color: labelColor,
      },
      '& svg': {
        color: brandColors.black,
      },
    },
    labelHidden: {
      visibility: 'hidden',
      height: 0,
    },
    placeholder: {
      color: labelColor,
    },
    menu: {
      '& .MuiMenuItem-root': {
        padding: `${typography.pxToRem(12)} ${typography.pxToRem(16)}`,
        '&:hover': {
          backgroundColor: brandColors.tintsChargeBlue80,
        },
      },
      '& .Mui-selected': { backgroundColor: brandColors.tintsMediumBlue80 },
      '& .Mui-focusVisible': {
        backgroundColor: 'initial',
      },
    },
    base: {
      '& .MuiSelect-select': {
        backgroundColor,
        color: labelColor,
      },
      '& legend': { display: 'none' },
      '& .MuiFormHelperText-root': {
        marginTop: typography.pxToRem(6),
        ...type['14_regular'],
        color: helperColor,
        '&.Mui-error': {
          color: errorBorderColor,
        },
      },
      '& .MuiOutlinedInput-root': {
        transition: 'border-color 0.2s ease, border-width 0.2s ease',

        '& fieldset': {
          top: 0,
          borderRadius: typography.pxToRem(8),
          border: `${typography.pxToRem(1)} solid ${defaultBorderColor}`,
        },

        // HOVER
        '&:hover fieldset': {
          borderColor: hoverBorderColor,
        },

        // FOCUS
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: typography.pxToRem(1.5),
          borderColor: focusBorderColor,
        },

        // ERROR always wins
        '&.Mui-error fieldset': {
          borderWidth: typography.pxToRem(1.5),
          borderColor: errorBorderColor,
        },
        // Ensure error style overrides focus style when both are applied
        '&.Mui-error.Mui-focused fieldset': {
          borderWidth: typography.pxToRem(1.5),
          borderColor: errorBorderColor,
        },

        // DISABLED always wins
        '&.Mui-disabled fieldset': {
          borderColor: defaultBorderColor,
          opacity: '0.4',
        },
      },
      '& .MuiSelect-icon': {
        marginRight: typography.pxToRem(16),
        width: `${typography.pxToRem(16)}!important`,
        right: 0,
        color: iconColor,
        transition: 'opacity 0.2s ease, color 0.2s ease',
        '&&': {
          pointerEvents: 'none',
          position: 'absolute',
          top: 'auto',
          color: 'inherit',
        },
      },
      '& .MuiOutlinedInput-root.Mui-disabled .MuiSelect-icon': {
        opacity: 0.4,
      },
      '& .MuiSelect-select.Mui-disabled': {
        '-webkit-text-fill-color': labelColor,
        opacity: 0.4,
      },
    },
    responsiveSizes: createResponsiveStyles(size, breakpoints, getSizeStyles),
  };
});

export default useStyles;
